// @ts-strict-ignore
import type { MultiSelectProps } from '@usealloy/component-library';

import { ActionType } from 'flow/store/engine/constants';
import type {
  Action,
  Attribute,
  PopulatedDataRoute,
  ImportedDataPassingObject,
  InputObjectInAction,
  MatrixModel,
  OutputObjectInAction,
  SchemaInstance,
  Services,
  WorkflowInfo,
  ServiceAttributesById,
} from 'flow/store/engine/types';
import {
  getActionDataObjectKeyName,
  getActionName, getActionTypeText, INIT_EXACT_AXIS, INIT_RANGE_AXIS,
  MatrixDataType,
} from 'flow/service/actions';
import { getActionTextStyles } from 'flow/editor/styles';
import { getAllowedAttributesForPortfolioWorkflow } from '~/flow/utils';

import { MULTISELECT_PROPS } from '../../../constants';
import type { NodeLineage } from '~/dashboard/views/workflows/service/graph';
import { canLinkToNode } from '../../../Graph/nodes/BaseNode/NodeConnector/helpers';

import type { Axis, AxisHeaderAttributeOption, AxisHeaderActionOption } from './types';
import {
  ActionDataType,
} from './types';
import { getAxisOptions, updateAxisSettings, updateMatrixSizeToMatchHeaders } from '../AxisConfigPopover/helpers';
import { getErrorAttributeLabel, isErrorAttribute } from 'flow/service/services';


export function transformActionToOption(action: Action): AxisHeaderActionOption {
  const actionName = getActionName(action);
  return {
    label: getActionTypeText(action),
    value: action.publicId,
    meta: {
      name: actionName,
      actionType: action.type,
      color: getActionTextStyles(action),
    },
  };
}

export function getAxisHeaderActionOptions({
  actions, focusedPublicId, nodeLineage,
}: {
  actions: SchemaInstance['actions'];
  focusedPublicId: Action['publicId'];
  nodeLineage: NodeLineage;
}): AxisHeaderActionOption[] {
  return Object.values(actions)
    .filter(action => (
      focusedPublicId !== action.publicId
      && action.type !== ActionType.OUTCOME
      && action.type !== ActionType.TAG
      && canLinkToNode(nodeLineage, action.publicId)
    ))
    .map(action => transformActionToOption(action))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function getAxisHeaderAttributeOptions({
  dataRoute, services, workflow, universalAttributes,
}: {
  dataRoute: PopulatedDataRoute;
  services: Services;
  workflow: WorkflowInfo;
  universalAttributes: ServiceAttributesById;
}): AxisHeaderAttributeOption[] {
  /*
    Temporarily hiding features for Portfolio Evaluations
    Epic ID: 109173
  */
  const allowedServiceAttributes: {
    [attribute_id: number]: Attribute;
  } = getAllowedAttributesForPortfolioWorkflow(
    workflow.type,
    services[dataRoute?.attribute?.service.internal_name]?.name,
    dataRoute?.attribute?.service.attributes,
  );

  const allowedAttributes = {
    ...allowedServiceAttributes,
    ...universalAttributes,
  };

  return Object.entries(
    allowedAttributes,
  ).sort((a, b) => a[1].display_name.localeCompare(b[1].display_name))
    .filter(([, attribute]) => (!attribute.archivedAt
      && (
        attribute.dataType === ActionDataType.INTEGER
        || attribute.dataType === ActionDataType.STRING
        || attribute.dataType === ActionDataType.DECIMAL
      )
    ))
    .map(([id, attribute]) => {
      const label = isErrorAttribute(attribute)
        ? getErrorAttributeLabel(attribute)
        : attribute.display_name;
      return {
        label,
        value: id,
        meta: {
        // For the future, when we want to truncate attribute names:
          shortenedName: attribute.display_name,
          dataType: attribute.dataType as ActionDataType,
          showAttributeWidget: isErrorAttribute(attribute),
          attribute,
        },
      };
    });
}

export function getMultiSelectProps(color?: string): Partial<MultiSelectProps> {
  const baseMultiSelectProps: Partial<MultiSelectProps> = {
    ...MULTISELECT_PROPS,
    customStyleFunctions: {
      ...MULTISELECT_PROPS.customStyleFunctions,
      menu: styles => ({
        ...styles,
        minWidth: '320px',
      }),
      placeholder: styles => ({
        ...styles,
        color: 'black',
        fontWeight: 'var(--cl-font-weight-bold)',
      }),
    },
  };

  return !color ? baseMultiSelectProps : {
    ...baseMultiSelectProps,
    customStyleFunctions: {
      ...baseMultiSelectProps.customStyleFunctions,
      singleValue: styles => ({
        ...baseMultiSelectProps.customStyleFunctions.singleValue(styles),
        color: color.split(': ')[1],
      }),
      option: (styles, props) => ({
        ...baseMultiSelectProps.customStyleFunctions.option(styles, props),
        color: color.split(': ')[1],
        lineHeight: 'var(--cl-line-height-1)',
      }),
    },
  };
}

export const getAxisDataRouteKey = (axis: Axis) => (axis === 'X'
  ? 'dataRouteX' : 'dataRouteY'
);

export const getLinkableActionDataType = (action: Action) => {
  const actionObjectKey = getActionDataObjectKeyName(action);
  switch (action.type) {
    case ActionType.IMPORTED_MATRIX_MODEL:
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE: {
      const importedObject = action[actionObjectKey] as ImportedDataPassingObject;
      return importedObject.dataType;
    }
    case ActionType.INPUT: {
      const inputObject = action[actionObjectKey] as InputObjectInAction;
      return inputObject.dataType;
    }
    case ActionType.MATRIX_MODEL: {
      const matrixModelObject = action[actionObjectKey] as MatrixModel;
      return matrixModelObject.matrixDataType;
    }
    case ActionType.OUTPUT: {
      const inputObject = action[actionObjectKey] as OutputObjectInAction;
      return inputObject.dataType;
    }
    default:
      return null;
  }
};

export const getMatrixAxisByActionDataType = (
  actionDataType: string,
): MatrixModel['axis']['x' | 'y'] => {
  switch (actionDataType) {
    case MatrixDataType.NUMBER:
    case ActionDataType.DECIMAL:
    case ActionDataType.INTEGER:
      return INIT_RANGE_AXIS;
    case ActionDataType.STRING:
      return INIT_EXACT_AXIS;
    default:
      return null;
  }
};

export const isAxisDataTypeDifferent = (prev, next) => (
  prev?.axisHeaderDataType !== next?.axisHeaderDataType
);

export const updateMatrixModelWithDataTypeChange = ({
  prev, next, axis, matrixModel,
}: {
  prev: MatrixModel['axis']['x' | 'y'];
  next: MatrixModel['axis']['x' | 'y'];
  axis: Axis;
  matrixModel: MatrixModel;
}): MatrixModel => {
  const axisKey = axis.toLowerCase();
  const { axisHeader } = prev;
  const prevAxisSettings = getAxisOptions(prev);
  const nextAxisSettings = getAxisOptions(next);

  const updatedAxis = updateAxisSettings({
    axisHeader,
    initialOptions: prevAxisSettings,
    selectedOptions: nextAxisSettings,
  });

  const updatedMatrix = updateMatrixSizeToMatchHeaders({
    axis, axisSettings: updatedAxis, matrix: matrixModel.matrix,
  });

  const updatedMatrixModel: MatrixModel = {
    ...matrixModel,
    axis: { ...matrixModel.axis, [axisKey]: updatedAxis },
    matrix: updatedMatrix,
  };

  return updatedMatrixModel;
};

// makes case insensitive and ':' and space agnostic
const cleanseString = str => str.replace(':', '').replace(' ', '').toLowerCase();

// lambda function to filters options
export function filterAxisOptions(option, inputtedValue): boolean {
  // We store just the action type in the label ("Service"/"Input"),
  // but we want to search by the action name as well (service name/input name).
  const completeLabel = `${option.label}${option.data.meta.name}`;
  return (
    cleanseString(completeLabel).includes(cleanseString(inputtedValue))
  );
}

